import { resolveCanonicalCategoryKey, resolveAppCategory } from "../apps/categoryNormalization";
import { addDays, parseDateKey, startOfWeek } from "../dateNav";
import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { formatDuration, todayKey } from "../format";
import {
  filterHabitsEntitiesForOther,
  topHabitsAppEntities,
} from "./unattributed";

// ---------------------------------------------------------------------------
// Shapes (mirror the convex/usageHeatmap.ts return shape)
// ---------------------------------------------------------------------------

export type HeatmapApp = {
  appIdentifier?: string;
  displayName: string;
  category?: string;
  durationSeconds: number;
  opens: number;
};

export type HeatmapDay = {
  date: string; // "YYYY-MM-DD"
  apps: HeatmapApp[];
};

export type HeatmapResult = {
  days: HeatmapDay[];
};

export type ViewMode = "app" | "category";
export type Metric = "time" | "opens";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** GitHub-style contribution window: 52 weeks (~12 months). */
export const HABIT_WEEKS = 52;

/** Maximum apps shown in App view mode (ranked by active metric). */
export const APP_MODE_TOP_N = 20;

/** Soft cap for tracked apps/categories — surfaced in the filter sheet. */
export const TRACKED_SOFT_CAP = 30;

/** Warn when the user is one or two selections away from the soft cap. */
export const TRACKED_SOFT_CAP_WARN_AT = TRACKED_SOFT_CAP - 2;

/** No-data cell: gray, fully opaque (NOT the app color). */
export const EMPTY_CELL_COLOR = "oklch(22% 0.01 165)";

/** Mon→Sun short weekday labels in the active locale (index 0 = Monday). */
export const WEEKDAY_LABELS: string[] = (() => {
  const monday = new Date(2024, 0, 1); // Jan 1 2024 is a Monday
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return day.toLocaleDateString(undefined, { weekday: "short" });
  });
})();

// ---------------------------------------------------------------------------
// Week-column grid (X axis = weeks oldest→newest, Y axis = Mon..Sun)
// ---------------------------------------------------------------------------

export type WeekDay = { date: string; inFuture: boolean };
export type WeekColumn = { weekStart: string; days: WeekDay[] };

export type WeekGrid = {
  columns: WeekColumn[];
  /** Oldest day (the first Monday) — query lower bound. */
  startDate: string;
  /** Reference day (today) — query upper bound. */
  endDate: string;
  /** Flat list of every date key in the grid, oldest first. */
  allDates: string[];
};

export function buildWeekGrid(referenceDate: string, weeks = HABIT_WEEKS): WeekGrid {
  const currentWeekStart = startOfWeek(referenceDate);
  const firstWeekStart = addDays(currentWeekStart, -(weeks - 1) * 7);
  const columns: WeekColumn[] = [];
  const allDates: string[] = [];

  for (let w = 0; w < weeks; w++) {
    const weekStart = addDays(firstWeekStart, w * 7);
    const days: WeekDay[] = [];
    for (let d = 0; d < 7; d++) {
      const date = addDays(weekStart, d);
      days.push({ date, inFuture: date > referenceDate });
      allDates.push(date);
    }
    columns.push({ weekStart, days });
  }

  return { columns, startDate: firstWeekStart, endDate: referenceDate, allDates };
}

/** Month label per column, shown only at the first column of each new month. */
export function monthLabelsForColumns(columns: WeekColumn[]): Array<string | null> {
  let lastMonth = -1;
  return columns.map((column) => {
    const monday = parseDateKey(column.days[0]!.date);
    const month = monday.getMonth();
    if (month !== lastMonth) {
      lastMonth = month;
      return monday.toLocaleDateString(undefined, { month: "short" });
    }
    return null;
  });
}

// ---------------------------------------------------------------------------
// Intensity scale (p90 ceiling, min non-zero floor)
// ---------------------------------------------------------------------------

export type IntensityScale = { floor: number; ceil: number };

export function intensityScale(values: number[]): IntensityScale {
  const nonZero = values.filter((value) => value > 0).sort((a, b) => a - b);
  if (nonZero.length === 0) return { floor: 0, ceil: 0 };
  const floor = nonZero[0]!;
  const p90Index = Math.floor(0.9 * (nonZero.length - 1));
  const ceil = nonZero[p90Index] ?? floor;
  return { floor, ceil };
}

/** Opacity in [0.15, 1] for a non-zero value; 0 means "render gray". */
export function cellOpacity(value: number, scale: IntensityScale): number {
  if (value <= 0) return 0;
  if (scale.ceil <= scale.floor) return 1;
  const t = Math.min((value - scale.floor) / (scale.ceil - scale.floor), 1);
  return 0.15 + t * 0.85;
}

// ---------------------------------------------------------------------------
// Aggregation: raw days → per-entity (app or category) daily series
// ---------------------------------------------------------------------------

export type HeatmapEntity = {
  /** Namespaced, mode-scoped filter key, e.g. "app:com.foo" or "cat:Social". */
  key: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
  isCategory: boolean;
  total: number;
  todayValue: number;
  byDate: Map<string, number>;
};

function metricValue(app: HeatmapApp, metric: Metric): number {
  return metric === "time" ? app.durationSeconds : app.opens;
}

export function aggregateEntities(
  days: HeatmapDay[],
  mode: ViewMode,
  metric: Metric,
  referenceDate: string,
): HeatmapEntity[] {
  const map = new Map<string, HeatmapEntity>();

  for (const day of [...days].sort((a, b) => a.date.localeCompare(b.date))) {
    for (const app of day.apps) {
      const value = metricValue(app, metric);

      let rawKey: string;
      let displayName: string;
      let appIdentifier: string | undefined;
      let category: string | undefined;

      if (mode === "app") {
        rawKey = app.appIdentifier?.trim() || app.displayName.trim();
        if (!rawKey) continue;
        displayName = app.displayName;
        appIdentifier = app.appIdentifier;
        category = app.category;
      } else {
        const canonicalKey = resolveCanonicalCategoryKey(app.category);
        rawKey = canonicalKey;
        displayName = canonicalKey;
        category = canonicalKey;
      }

      const key = `${mode === "app" ? "app" : "cat"}:${rawKey}`;
      let entity = map.get(key);
      if (!entity) {
        entity = {
          key,
          displayName,
          appIdentifier,
          category,
          isCategory: mode === "category",
          total: 0,
          todayValue: 0,
          byDate: new Map(),
        };
        map.set(key, entity);
      }

      if (mode === "app") {
        entity.category = resolveAppCategory(
          entity.category,
          app.category,
          entity.appIdentifier ?? app.appIdentifier,
        );
      }

      entity.total += value;
      entity.byDate.set(day.date, (entity.byDate.get(day.date) ?? 0) + value);
    }
  }

  const entities = [...map.values()];
  for (const entity of entities) {
    entity.todayValue = entity.byDate.get(referenceDate) ?? 0;
  }
  return entities.sort((a, b) => b.total - a.total);
}

/** Keep only the top-N apps in App mode; category mode is uncapped. */
export function limitAggregatedEntities(
  entities: HeatmapEntity[],
  mode: ViewMode,
  limit = APP_MODE_TOP_N,
): HeatmapEntity[] {
  if (mode !== "app") return entities;
  return entities.slice(0, limit);
}

/**
 * Default view: top-N by active metric. Tracked view: only selected keys, including
 * apps outside the top-N slice when they exist in the full heatmap aggregate.
 */
export function resolveVisibleEntities(
  allEntities: HeatmapEntity[],
  mode: ViewMode,
  trackedKeys: string[],
  includeOther = false,
): HeatmapEntity[] {
  const prefix = mode === "app" ? "app:" : "cat:";
  const selectedKeys = trackedKeys.filter((key) => key.startsWith(prefix));
  if (selectedKeys.length === 0) {
    return topHabitsAppEntities(allEntities, mode, includeOther);
  }

  const filteredEntities = filterHabitsEntitiesForOther(allEntities, mode, includeOther);
  const entityByKey = new Map(filteredEntities.map((entity) => [entity.key, entity]));
  const visible: HeatmapEntity[] = [];
  const seen = new Set<string>();

  for (const key of selectedKeys) {
    const entity = entityByKey.get(key);
    if (!entity || seen.has(key)) continue;
    visible.push(entity);
    seen.add(key);
  }

  return visible.sort((a, b) => b.total - a.total);
}

// ---------------------------------------------------------------------------
// Per-entity day lookup (used by the drawer for mini heatmap + calendar)
// ---------------------------------------------------------------------------

export type EntityRef = {
  appIdentifier?: string;
  displayName: string;
  category?: string;
  isCategory: boolean;
};

export function dayValueForEntity(
  apps: HeatmapApp[],
  entity: EntityRef,
): { seconds: number; opens: number } {
  let seconds = 0;
  let opens = 0;

  if (entity.isCategory) {
    const cat = resolveCanonicalCategoryKey(entity.category ?? entity.displayName);
    for (const app of apps) {
      const appCat = resolveCanonicalCategoryKey(app.category);
      if (appCat === cat) {
        seconds += app.durationSeconds;
        opens += app.opens;
      }
    }
  } else {
    const key = entity.appIdentifier?.trim() || entity.displayName.trim();
    for (const app of apps) {
      const appKey = app.appIdentifier?.trim() || app.displayName.trim();
      if (appKey === key) {
        seconds += app.durationSeconds;
        opens += app.opens;
      }
    }
  }

  return { seconds, opens };
}

/** Build a per-date value map for one entity from raw heatmap days. */
export function entityValuesByDate(
  days: HeatmapDay[],
  entity: EntityRef,
  metric: Metric,
): Map<string, number> {
  const byDate = new Map<string, number>();
  for (const day of days) {
    const { seconds, opens } = dayValueForEntity(day.apps, entity);
    byDate.set(day.date, metric === "time" ? seconds : opens);
  }
  return byDate;
}

// ---------------------------------------------------------------------------
// Month calendar grid (drawer)
// ---------------------------------------------------------------------------

export type MonthCell = { date: string; inMonth: boolean; inFuture: boolean };

export function buildMonthGrid(
  monthAnchor: string,
  referenceDate: string,
): { weeks: MonthCell[][]; monthLabel: string } {
  const anchor = parseDateKey(monthAnchor);
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const lastKey = todayKey(lastOfMonth);

  const weeks: MonthCell[][] = [];
  let cursor = startOfWeek(todayKey(firstOfMonth)); // Monday on/before the 1st

  for (let guard = 0; guard < 6; guard++) {
    const week: MonthCell[] = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(cursor, i);
      week.push({
        date,
        inMonth: parseDateKey(date).getMonth() === month,
        inFuture: date > referenceDate,
      });
    }
    weeks.push(week);
    if (week[6]!.date >= lastKey) break;
    cursor = addDays(cursor, 7);
  }

  return {
    weeks,
    monthLabel: firstOfMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
  };
}

export function addMonths(monthAnchor: string, delta: number): string {
  const date = parseDateKey(monthAnchor);
  return todayKey(new Date(date.getFullYear(), date.getMonth() + delta, 1));
}

/** First-of-month date key for the month containing `dateKey`. */
export function firstOfMonth(dateKey: string): string {
  return addMonths(dateKey, 0);
}

export function monthRange(
  monthAnchor: string,
  referenceDate: string,
): { startDate: string; endDate: string } {
  const date = parseDateKey(monthAnchor);
  const first = todayKey(new Date(date.getFullYear(), date.getMonth(), 1));
  const lastFull = todayKey(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return { startDate: first, endDate: lastFull > referenceDate ? referenceDate : lastFull };
}

/** Can the user step forward to `monthAnchor` without entering a future month? */
export function isViewableMonth(monthAnchor: string, referenceDate: string): boolean {
  const anchor = parseDateKey(monthAnchor);
  const reference = parseDateKey(referenceDate);
  if (anchor.getFullYear() !== reference.getFullYear()) {
    return anchor.getFullYear() < reference.getFullYear();
  }
  return anchor.getMonth() <= reference.getMonth();
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** Locale-aware duration for drawer day-detail style. */
export function formatHrMin(seconds: number, languageCode: Screen0LanguageCode = "en"): string {
  return formatDuration(seconds, languageCode);
}

export function formatOpens(opens: number): string {
  const rounded = Math.round(opens);
  return `${rounded} ${rounded === 1 ? "open" : "opens"}`;
}
